import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { AlertTriangle, RefreshCw, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";

/**
 * iter107 / Session J+ — Payment Integrity console.
 *
 * Shows every persisted orphan-payment incident (successful Razorpay
 * payment that could not be turned into an internal order). Each row
 * carries the identifiers ops need to reconcile on the Razorpay
 * dashboard + a one-click idempotent recovery button that reuses the
 * existing /api/admin/payments/recover-by-payment-id endpoint.
 *
 * The list is read-only apart from Recover — no card details, no secrets.
 */
export default function AdminPaymentIntegrity() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/payments/orphans/incidents");
      setRows(r.data?.incidents || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const recover = async (pid) => {
    setRecovering((s) => ({ ...s, [pid]: true }));
    try {
      const r = await api.post("/admin/payments/recover-by-payment-id", {
        razorpay_payment_id: pid,
      });
      if (r.data?.already_recovered) {
        toast.success("Already recovered — order exists");
      } else if (r.data?.recovered) {
        toast.success(`Recovered — order_id ${r.data.order_id}`);
      } else {
        toast.success("Recovery completed");
      }
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecovering((s) => ({ ...s, [pid]: false }));
    }
  };

  const unresolved = rows.filter((r) => r.status === "unresolved");
  const resolved = rows.filter((r) => r.status === "resolved");

  return (
    <div className="space-y-8" data-testid="admin-payment-integrity">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-brand-dark flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-red-600" strokeWidth={1.5} />
            Payment Integrity
          </h1>
          <p className="text-sm text-brand-muted mt-1 max-w-2xl">
            Successful Razorpay payments that did NOT produce a matching internal website order.
            Recovery is idempotent — clicking it twice will not create a duplicate order.
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm text-brand-dark hover:text-brand-chili"
          data-testid="orphan-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} /> Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-brand-muted">Loading…</p>}

      {!loading && unresolved.length === 0 && (
        <div
          className="bg-white border border-brand-line rounded-2xl p-8 text-center"
          data-testid="orphan-empty-state"
        >
          <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-600 mb-3" strokeWidth={1.5} />
          <p className="font-semibold text-brand-dark">No unresolved orphan payments</p>
          <p className="text-sm text-brand-muted mt-1">
            Every successful Razorpay payment currently has a matching internal order.
          </p>
        </div>
      )}

      {unresolved.length > 0 && (
        <section data-testid="orphan-unresolved-section">
          <h2 className="text-lg font-semibold text-brand-dark mb-3">
            Unresolved ({unresolved.length})
          </h2>
          <div className="bg-white border border-red-200 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-red-50 text-red-900 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Detected</th>
                  <th className="px-4 py-3 text-left">Payment</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Customer</th>
                  <th className="px-4 py-3 text-left">Alert email</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {unresolved.map((row) => (
                  <tr key={row.razorpay_payment_id} className="border-t border-red-100" data-testid={`orphan-row-${row.razorpay_payment_id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-brand-muted">
                      {new Date(row.detected_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">
                      <div>{row.razorpay_payment_id}</div>
                      {row.razorpay_order_id && (
                        <div className="text-brand-muted mt-0.5">{row.razorpay_order_id}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-semibold">₹{Number(row.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-brand-muted">
                      {row.customer_name || row.customer_email || row.customer_phone ? (
                        <>
                          {row.customer_name && <div>{row.customer_name}</div>}
                          {row.customer_email && <div className="text-xs">{row.customer_email}</div>}
                          {row.customer_phone && <div className="text-xs">{row.customer_phone}</div>}
                        </>
                      ) : (
                        <span className="italic text-xs">Unknown — check Razorpay</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-brand-muted">
                      {row.alert_sent_at ? (
                        <span className="inline-flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                          Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => recover(row.razorpay_payment_id)}
                        disabled={!!recovering[row.razorpay_payment_id]}
                        className="inline-flex items-center gap-2 bg-brand-chili text-white text-xs font-semibold px-3 py-1.5 rounded-md hover:opacity-90 disabled:opacity-50"
                        data-testid={`orphan-recover-${row.razorpay_payment_id}`}
                      >
                        {recovering[row.razorpay_payment_id] ? "Recovering…" : "Recover"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {resolved.length > 0 && (
        <section data-testid="orphan-resolved-section">
          <h2 className="text-lg font-semibold text-brand-dark mb-3">
            Resolved ({resolved.length})
          </h2>
          <div className="bg-white border border-brand-line rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-subtle text-brand-dark text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">Detected</th>
                  <th className="px-4 py-3 text-left">Payment</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Recovered order</th>
                  <th className="px-4 py-3 text-left">Resolved at</th>
                </tr>
              </thead>
              <tbody>
                {resolved.map((row) => (
                  <tr key={row.razorpay_payment_id} className="border-t border-brand-line">
                    <td className="px-4 py-3 whitespace-nowrap text-brand-muted">
                      {new Date(row.detected_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{row.razorpay_payment_id}</td>
                    <td className="px-4 py-3">₹{Number(row.amount || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {row.resolved_by_order_id || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-brand-muted">
                      {row.resolved_at ? new Date(row.resolved_at).toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
